import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNavigate } from "react-router-dom";
import MenuBar from "@/components/MenuBar";
import { ChevronRight, Menu } from "lucide-react";
import { EndpointKeys } from "@/config/api";
import ApiService from "@/services/api";
import { EmployeesResponse, Employee } from "@/types/api";
import { getEmployees, getActiveEmployees } from "@/services/employeeService";
import { useAuth } from "@/hooks/useAuth";
import { isSuperUser } from "@/types/user";
import SuperUserEmployerSelector from "@/components/common/SuperUserEmployerSelector";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info } from "lucide-react";
import { useAddressValidation } from "@/hooks/useAddressValidation";
import { useEmployees } from "@/contexts/EmployeeContext";
import { Switch } from "@/components/ui/switch";
import BillingAPIService from "@/services/billing-api";
import { useLocation } from "react-router-dom";
import type { SeatSummary } from "@/types/billing";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface Employees { employees: Employee[] }

const phoneRegex = /^\+?[1-9]\d{1,14}$/;

// Dynamic schema creation based on country-specific validation
const createFormSchema = (addressValidation: any) => {
  return z.object({
    firstName: z
      .string()
      .min(1, "First name is required")
      .max(35, "First name must be 35 characters or less")
      .regex(/^[A-Za-z]+$/, "First name must only contain alphabetic characters"),
    lastName: z
      .string()
      .min(1, "Last name is required")
      .max(35, "Last name must be 35 characters or less")
      .regex(/^[A-Za-z]+$/, "Last name must only contain alphabetic characters"),
    homeAddress: z.object({
      street_number: z
        .string()
        .min(1, "Street number is required")
        .regex(
          addressValidation.rules.streetNumberPattern || /^[0-9A-Za-z]+$/,
          addressValidation.rules.streetNumberErrorMessage || "Invalid street number format"
        ),
      route: z.string().min(1, "Street name is required"),
      locality: z.string().min(1, "Town/City is required"),
      state: z.string().optional(),
      postal_code: z
        .string()
        .regex(
          addressValidation.rules.postalCodePattern,
          addressValidation.rules.postalCodeErrorMessage
        ),
      country: z.string().default("United Kingdom"),
    }),
    phoneNumber: z
      .string()
      .regex(phoneRegex, "Invalid phone number")
      .optional()
      .or(z.literal("")),
    gender: z.enum(["M", "F", "O"]),
    dob: z.string().superRefine((date: string, ctx: z.RefinementCtx) => {
      const today = new Date();
      const birthDate = new Date(date);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }
      if (age < 18) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Employee must be at least 18 years old",
        });
      }
      if (age > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Employee cannot be older than 100 years",
        });
      }
    }),
  });
};

const EmployeeManagement: React.FC = () => {
  const { employees, includeTerminated, setIncludeTerminated, refresh } = useEmployees();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [seatSummary, setSeatSummary] = useState<SeatSummary | null>(null);
  const [loadingSeatSummary, setLoadingSeatSummary] = useState<boolean>(false);
  const location = useLocation();
  const manageSeatsRequested = new URLSearchParams(location.search).get("manageSeats") === "1";
  const [seatModalOpen, setSeatModalOpen] = useState<boolean>(false);

  // Address validation hook for country-specific validation
  const addressValidation = useAddressValidation();
  
  // Create schema with country-specific validation
  const formSchema = createFormSchema(addressValidation);
  type FormValues = z.infer<typeof formSchema>;

  // Superuser specific state
  const isSuperUserAccount = isSuperUser(user);
  const [selectedUserId, setSelectedUserId] = useState<string>("current");
  const [hasSelectedEmployer, setHasSelectedEmployer] = useState(false);

  useEffect(() => {
    if (isSuperUserAccount && selectedUserId !== "current") {
      setHasSelectedEmployer(true);
    }
  }, [selectedUserId, isSuperUserAccount]);

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId);
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      homeAddress: {
        street_number: "",
        route: "",
        locality: "",
        state: "",
        postal_code: "",
        country: "United Kingdom",
      },
      phoneNumber: "",
      gender: "M",
      dob: "",
    },
  });

  const { formState } = form;

  // Keep URL/userId and refresh employees via context
  useEffect(() => {
    const newUrl = new URL(window.location.href);
    if (isSuperUserAccount && selectedUserId !== "current") {
      newUrl.searchParams.set("userId", selectedUserId);
    } else {
      newUrl.searchParams.delete("userId");
    }
    window.history.replaceState({}, "", newUrl.toString());

    if (!isSuperUserAccount || hasSelectedEmployer) {
      void refresh({ userId: isSuperUserAccount ? selectedUserId : undefined });
    }
  }, [selectedUserId, isSuperUserAccount, hasSelectedEmployer, refresh]);

  // Fetch seat summary on mount/tenant change
  useEffect(() => {
    const fetchSeats = async () => {
      try {
        setLoadingSeatSummary(true);
        const summary = await BillingAPIService.getSeatSummary(
          isSuperUserAccount && selectedUserId !== "current"
            ? Number(selectedUserId)
            : undefined,
        );
        setSeatSummary(summary);
      } catch (e) {
        console.error("Failed to fetch seat summary", e);
      } finally {
        setLoadingSeatSummary(false);
      }
    };
    // Only fetch when employer scope is known
    if (!isSuperUserAccount || hasSelectedEmployer) {
      void fetchSeats();
    }
  }, [isSuperUserAccount, selectedUserId, hasSelectedEmployer]);

  // Open seat modal if deep-linked from dashboard
  useEffect(() => {
    if (manageSeatsRequested) {
      setSeatModalOpen(true);
    }
  }, [manageSeatsRequested]);

  const onSubmit = async (data: FormValues) => {
    try {
      // Preflight seat check
      if (seatSummary && seatSummary.available_seats < 1) {
        const resp = await BillingAPIService.createSeatCheckout(
          isSuperUserAccount && selectedUserId !== "current"
            ? Number(selectedUserId)
            : undefined,
          1,
        );
        window.location.href = resp.checkoutUrl;
        return;
      }
      await ApiService.request(EndpointKeys.CREATE_EMPLOYEE, {
        method: "POST",
        body: data,
        ...(isSuperUserAccount &&
          selectedUserId !== "current" && {
            queryParams: { userId: selectedUserId },
          }),
      });

      void refresh({ userId: isSuperUserAccount ? selectedUserId : undefined });
      form.reset();
      setIsDialogOpen(false);
      // refresh seat summary after hire
      try {
        const summary = await BillingAPIService.getSeatSummary(
          isSuperUserAccount && selectedUserId !== "current"
            ? Number(selectedUserId)
            : undefined,
        );
        setSeatSummary(summary);
      } catch {}
    } catch (error) {
      console.error("Error creating employee:", error);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1 md:ml-0 overflow-hidden mt-16 md:mt-0">
        <div className="p-4 md:p-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="text-2xl font-bold">Employee Management</h2>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={includeTerminated}
                    onCheckedChange={(v) => setIncludeTerminated(!!v)}
                    id="show-terminated-toggle"
                  />
                  <label htmlFor="show-terminated-toggle" className="text-sm text-muted-foreground">
                    Show terminated
                  </label>
                </div>
                {/* Hire Employee dialog with seat availability check */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                          <DialogTrigger asChild>
                            <Button 
                              onClick={() => setIsDialogOpen(true)}
                              disabled={seatSummary?.available_seats === 0}
                            >
                              Hire Employee
                            </Button>
                          </DialogTrigger>

                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create New Employee</DialogTitle>
                    </DialogHeader>
                    <Form {...form}>
                      <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-4 mt-4"
                      >
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="spacey4">
                              <FormField
                                control={form.control}
                                name="firstName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>First Name</FormLabel>
                                    <FormControl>
                                      <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name="lastName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Last Name</FormLabel>
                                    <FormControl>
                                      <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name="phoneNumber"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Phone Number</FormLabel>
                                    <FormControl>
                                      <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name="gender"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Gender</FormLabel>
                                    <Select
                                      onValueChange={field.onChange}
                                      defaultValue={field.value}
                                    >
                                      <FormControl>
                                        <SelectTrigger>
                                          <SelectValue placeholder="Select gender" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="M">Male</SelectItem>
                                        <SelectItem value="F">
                                          Female
                                        </SelectItem>
                                        <SelectItem value="O">Other</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name="dob"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Date of Birth</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="date"
                                        {...field}
                                        max={
                                          new Date(
                                            new Date().getFullYear() - 18,
                                            new Date().getMonth(),
                                            new Date().getDate(),
                                          )
                                            .toISOString()
                                            .split("T")[0]
                                        }
                                        min={
                                          new Date(
                                            new Date().getFullYear() - 100,
                                            new Date().getMonth(),
                                            new Date().getDate(),
                                          )
                                            .toISOString()
                                            .split("T")[0]
                                        }
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            <div className="spacey4">
                              <FormField
                                control={form.control}
                                name="homeAddress.street_number"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Street Number</FormLabel>
                                    <FormControl>
                                      <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name="homeAddress.route"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Street Name</FormLabel>
                                    <FormControl>
                                      <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name="homeAddress.locality"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Town/City</FormLabel>
                                    <FormControl>
                                      <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name="homeAddress.state"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>County</FormLabel>
                                    <FormControl>
                                      <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name="homeAddress.postal_code"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Postcode</FormLabel>
                                    <FormControl>
                                      <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name="homeAddress.country"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Country</FormLabel>
                                    <FormControl>
                                      <Input {...field} readOnly={true} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>
                          <Button type="submit" disabled={!formState.isValid}>
                            Create Employee
                          </Button>
                        </form>
                      </Form>
                    </DialogContent>
                  </Dialog>
                      </div>
                    </TooltipTrigger>
                    {seatSummary?.available_seats === 0 && (
                      <TooltipContent>
                        <p>You must purchase another seat to hire a new employee</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>

              <div className="pt-4 space-y-6">
                <Alert className="my-6">
                  <Info className="h-4 w-4 text-primary" />
                  <AlertTitle>Employee Management Overview</AlertTitle>
                  <AlertDescription>
                    Manage your nanny's employment details in one place. You can
                    track essential information including personal details,
                    salary arrangements, tax status, and employment history.
                    While Hirelo automatically handles ongoing tax compliance
                    and HMRC submissions, you can always review and update
                    information here as needed.
                  </AlertDescription>
                </Alert>

                {/* Seats summary banner */}
                <Alert className="my-4">
                  <AlertTitle>Seats</AlertTitle>
                  <AlertDescription>
                    {loadingSeatSummary && <span>Loading seat usage…</span>}
                    {!loadingSeatSummary && seatSummary && (
                      <div className="flex flex-wrap items-center gap-2">
                        <span>
                          Active {seatSummary.active_employees} / Paid {seatSummary.paid_seats}
                        </span>
                        <Badge variant={seatSummary.available_seats > 0 ? "default" : "destructive"}>
                          Available {seatSummary.available_seats}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSeatModalOpen(true)}
                        >
                          Manage seats
                        </Button>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>

                {/* Manage seats modal (simple CTA to Stripe) */}
                {seatModalOpen && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
                      <h3 className="text-lg font-semibold mb-2">Manage seats</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Add an additional seat to hire a new employee. You’ll be redirected to checkout.
                      </p>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setSeatModalOpen(false)}>
                          Cancel
                        </Button>
                        <Button
                          onClick={async () => {
                            try {
                              const resp = await BillingAPIService.createSeatCheckout(
                                isSuperUserAccount && selectedUserId !== "current"
                                  ? Number(selectedUserId)
                                  : undefined,
                                1,
                              );
                              window.location.href = resp.checkoutUrl;
                            } catch (e) {
                              console.error("Failed to start seat checkout", e);
                            }
                          }}
                        >
                          Proceed to checkout
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Add the SuperUser selector in desktop view */}
              {isSuperUserAccount && (
                <div className="mt-6">
                  <SuperUserEmployerSelector
                    selectedUserId={selectedUserId}
                    onUserChange={handleUserChange}
                  />
                  <p className="text-sm text-muted-foreground mt-2">
                    Select an employer to view their employees
                  </p>
                </div>
              )}
            </CardHeader>

            <CardContent>
              {/* If superuser without selected employer, show selection prompt */}
              {isSuperUserAccount && !hasSelectedEmployer ? (
                <div className="flex flex-col items-center justify-center p-12 text-center">
                  <div className="mb-4 rounded-full bg-yellow-100 p-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8 text-yellow-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <h2 className="mb-2 text-xl font-semibold">
                    Please Select an Employer
                  </h2>
                  <p className="max-w-md text-gray-600">
                    As a superuser, you need to select an employer from the
                    dropdown above before viewing data.
                  </p>
                </div>
              ) : (
                <div className="w-full overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>First Name</TableHead>
                        <TableHead>Last Name</TableHead>
                        <TableHead>Home Address</TableHead>
                        <TableHead>Phone Number</TableHead>
                        <TableHead>Gender</TableHead>
                        <TableHead>Date of Birth</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {employees.map((employee) => (
                        <TableRow
                          key={employee.id}
                          onClick={() => {
                            // When a superuser is viewing another user's employees, include the userId in the URL
                            if (
                              isSuperUserAccount &&
                              selectedUserId !== "current"
                            ) {
                              navigate(
                                `/employee/${employee.id}?userId=${selectedUserId}`,
                              );
                            } else {
                              navigate(`/employee/${employee.id}`);
                            }
                          }}
                          className={`cursor-pointer hover:bg-gray-100 ${employee.isActive ? "" : "opacity-70"}`}
                        >
                          <TableCell>{employee.firstName}</TableCell>
                          <TableCell>{employee.lastName}</TableCell>
                          <TableCell>
                            {`${employee.homeAddress.street_number} ${employee.homeAddress.route}, ${employee.homeAddress.locality}, ${employee.homeAddress.postal_code}`}
                          </TableCell>
                          <TableCell>{employee.phoneNumber}</TableCell>
                          <TableCell>{employee.gender}</TableCell>
                          <TableCell>
                            {new Date(
                              employee.dob + "T00:00:00",
                            ).toLocaleDateString("en-GB")}
                          </TableCell>
                          <TableCell>
                            {employee.isActive ? (
                              <Badge variant="default">Active</Badge>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary">Terminated</Badge>
                                {employee.terminationDate && (
                                  <span className="text-xs text-muted-foreground">
                                    {new Date(employee.terminationDate + "T00:00:00").toLocaleDateString("en-GB")}
                                  </span>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          </TableCell>
                        </TableRow>
                      ))}
                      {employees.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            className="text-center text-muted-foreground"
                          >
                            No employees found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EmployeeManagement;
